// src/lib/compliance.js
// ─────────────────────────────────────────────────────────────────────────────
// Compliance utilities for CosmiqHerbz.
// Handles: country rule checks, audit logging, compliance event recording,
// age gate event logging. All writes use Supabase directly — no server needed.
// ─────────────────────────────────────────────────────────────────────────────
import { supabase } from './supabase';

export const GATE_VERSION = '1.1';

// ── Session ID ────────────────────────────────────────────────────────────────
// Stable per-browser-session fingerprint — not tied to user identity.
export function getSessionId() {
  const KEY = 'cq_session_id';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

// ── Age gate event logging ────────────────────────────────────────────────────
/**
 * Log an age gate interaction to age_gate_events.
 * Called from AgeGate.js on every user action.
 * @param {'passed'|'declined'|'exited'} result
 * @param {string} country  — e.g. 'DE'
 * @param {string} lang     — e.g. 'en'
 * @param {string|null} userId — auth user id if already logged in
 */
export async function logAgeGateEvent(result, country, lang, userId = null) {
  try {
    await supabase.from('age_gate_events').insert({
      user_id:     userId,
      session_id:  getSessionId(),
      result,
      country:     country || 'DE',
      lang:        lang    || 'en',
      gate_version: GATE_VERSION,
    });
  } catch (e) {
    // Never throw — logging must not block UX
    console.warn('[Compliance] age_gate_events insert failed:', e?.message);
  }
}

// ── Compliance event recording ────────────────────────────────────────────────
/**
 * Record a compliance event (block, restriction, warning).
 * @param {string} eventType — 'order_blocked' | 'product_restricted' | 'checkout_blocked' | ...
 * @param {object} details
 */
export async function logComplianceEvent(eventType, {
  userId = null,
  country = null,
  productId = null,
  orderId = null,
  ruleId = null,
  details = {},
} = {}) {
  try {
    await supabase.from('compliance_events').insert({
      event_type:  eventType,
      user_id:     userId,
      session_id:  getSessionId(),
      country,
      product_id:  productId,
      order_id:    orderId,
      rule_id:     ruleId,
      details,
    });
  } catch (e) {
    console.warn('[Compliance] compliance_events insert failed:', e?.message);
  }
}

// ── Audit logging ─────────────────────────────────────────────────────────────
/**
 * Write an immutable audit log entry.
 * Use this for any admin action that modifies data.
 * @param {object} actor — { id, email } from profile/user
 * @param {string} action — dot-notation e.g. 'product.edit', 'order.status_change'
 * @param {string} entityType — 'product' | 'order' | 'discount_code' | 'country_rule' | ...
 * @param {string} entityId — UUID or code of affected record
 * @param {object|null} before — snapshot before change
 * @param {object|null} after  — snapshot after change
 * @param {string} [notes]
 */
export async function writeAuditLog(actor, action, entityType, entityId, before = null, after = null, notes = '') {
  try {
    const { error } = await supabase.rpc('write_audit_log', {
      p_actor_id:    actor?.id    || null,
      p_actor_email: actor?.email || 'system',
      p_action:      action,
      p_entity_type: entityType,
      p_entity_id:   String(entityId),
      p_before_json: before  ? JSON.parse(JSON.stringify(before)) : null,
      p_after_json:  after   ? JSON.parse(JSON.stringify(after))  : null,
      p_notes:       notes   || null,
    });
    if (error) console.warn('[Compliance] audit_log rpc failed:', error.message);
  } catch (e) {
    console.warn('[Compliance] writeAuditLog exception:', e?.message);
  }
}

// ── Country rule checks ───────────────────────────────────────────────────────
/** Fetch all active country rules (cached per session) */
let _rulesCache = null;
let _rulesCacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export async function fetchCountryRules() {
  const now = Date.now();
  if (_rulesCache && now - _rulesCacheTime < CACHE_TTL) return _rulesCache;
  const { data } = await supabase
    .from('product_country_rules')
    .select('*');
  _rulesCache = data || [];
  _rulesCacheTime = now;
  return _rulesCache;
}

/** Invalidate rules cache (call after admin saves a rule) */
export function invalidateRulesCache() { _rulesCache = null; }

/** Fetch site-wide blocked countries */
let _blocksCache = null;
let _blocksCacheTime = 0;
export async function fetchCountryBlocks() {
  const now = Date.now();
  if (_blocksCache && now - _blocksCacheTime < CACHE_TTL) return _blocksCache;
  const { data } = await supabase
    .from('country_block_list')
    .select('*')
    .eq('active', true);
  _blocksCache = data || [];
  _blocksCacheTime = now;
  return _blocksCache;
}
export function invalidateBlocksCache() { _blocksCache = null; }

/**
 * Check if a specific product is available in a country.
 * Returns: { allowed: bool, rule: object|null, type: 'blocked'|'restricted'|'allowed'|null }
 */
export async function checkProductInCountry(productId, countryCode) {
  const rules = await fetchCountryRules();
  const rule = rules.find(r => r.product_id === productId && r.country_code === countryCode);
  if (!rule) return { allowed: true, rule: null, type: null };
  if (rule.rule_type === 'blocked')     return { allowed: false, rule, type: 'blocked' };
  if (rule.rule_type === 'restricted')  return { allowed: true,  rule, type: 'restricted' };
  if (rule.rule_type === 'allowed')     return { allowed: true,  rule, type: 'allowed' };
  return { allowed: true, rule, type: null };
}

/**
 * Check if checkout is allowed for this country at all.
 * Returns: { allowed: bool, reason: string|null }
 */
export async function checkCountryAllowed(countryCode) {
  const blocks = await fetchCountryBlocks();
  const block = blocks.find(b => b.country_code === countryCode);
  if (block) return { allowed: false, reason: block.reason };
  return { allowed: true, reason: null };
}

/**
 * Validate an entire cart against country rules.
 * Returns: { valid: bool, blockedItems: [], restrictedItems: [] }
 */
export async function validateCartForCountry(cartItems, countryCode) {
  const [rules, blocks] = await Promise.all([
    fetchCountryRules(),
    fetchCountryBlocks(),
  ]);

  const countryBlock = blocks.find(b => b.country_code === countryCode);
  if (countryBlock) {
    return {
      valid: false,
      countryBlocked: true,
      countryBlockReason: countryBlock.reason,
      blockedItems: cartItems,
      restrictedItems: [],
    };
  }

  const blockedItems     = [];
  const restrictedItems  = [];

  for (const item of cartItems) {
    const rule = rules.find(r => r.product_id === item.id && r.country_code === countryCode);
    if (!rule) continue;
    if (rule.rule_type === 'blocked')    blockedItems.push({ ...item, rule });
    if (rule.rule_type === 'restricted') restrictedItems.push({ ...item, rule });
  }

  return {
    valid:             blockedItems.length === 0,
    countryBlocked:    false,
    countryBlockReason: null,
    blockedItems,
    restrictedItems,
  };
}
